import { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { NAV_LINKS } from '../constants/content';
import { SITE_NAME } from '../constants/site';
import Logo from './Logo';

function NavItem({ link, onNavigate }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  if (!link.children) {
    return (
      <li className="nav__item">
        <NavLink to={link.to} end={link.to === '/'} className="nav__link" onClick={onNavigate}>
          {link.label}
        </NavLink>
      </li>
    );
  }

  const active = pathname === link.to;

  return (
    <li className={`nav__item nav__item--parent ${open ? 'is-open' : ''}`}>
      <div className="nav__parent-row">
        <Link
          to={link.to}
          className={`nav__link ${active ? 'active' : ''}`}
          onClick={onNavigate}
        >
          {link.label}
        </Link>
        <button
          type="button"
          className="nav__caret"
          aria-label={`Show ${link.label} menu`}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <svg viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
            <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </button>
      </div>
      <ul className="nav__sub">
        {link.children.map((child) => (
          <li key={child.to}>
            <Link to={child.to} onClick={onNavigate}>
              {child.label}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const close = () => setMenuOpen(false);

  const handleSearch = (event) => {
    event.preventDefault();
    const q = String(new FormData(event.currentTarget).get('q') || '').trim();
    setSearchOpen(false);
    close();
    navigate(q ? `/blogs?q=${encodeURIComponent(q)}` : '/blogs');
  };

  return (
    <header className="site-header">
      <div className="site-branding">
        <Link to="/" className="site-logo" aria-label={`${SITE_NAME} home`}>
          <Logo size={92} />
          <span className="site-logo__name">Family Tree Maker</span>
          <span className="site-logo__tag">Help Center</span>
        </Link>
      </div>

      <nav className="main-nav" aria-label="Primary">
        <div className="main-nav__inner">
          <button
            type="button"
            className="menu-toggle"
            aria-expanded={menuOpen}
            aria-controls="primary-menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="menu-toggle__bars" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            Menu
          </button>

          <ul id="primary-menu" className={`nav ${menuOpen ? 'is-open' : ''}`}>
            {NAV_LINKS.map((link) => (
              <NavItem key={link.label} link={link} onNavigate={close} />
            ))}
          </ul>

          <button
            type="button"
            className="nav__search-toggle"
            aria-label={searchOpen ? 'Close search' : 'Search guides'}
            aria-expanded={searchOpen}
            onClick={() => setSearchOpen((v) => !v)}
          >
            {searchOpen ? (
              <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="2" />
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                <circle cx="8.5" cy="8.5" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M13 13l5 5" stroke="currentColor" strokeWidth="2" />
              </svg>
            )}
          </button>
        </div>

        {searchOpen ? (
          <form className="nav-search" role="search" onSubmit={handleSearch}>
            <label htmlFor="site-search" className="sr-only">
              Search guides
            </label>
            <input
              id="site-search"
              name="q"
              type="search"
              placeholder="Search help guides — try sync, install, or TreeVault"
              autoFocus
            />
            <button type="submit">Search</button>
          </form>
        ) : null}
      </nav>
    </header>
  );
}
